import { useT } from '@transifex/react'
import { vn } from 'vend-number'
import { Finals } from '../../../models/sale'
import { useAppSelector } from '../../../state/hooks'

import { selectSaleFinals } from '../../../state/slices/currentSale/currentSaleSlice'

/**
 * Determines unique surcharge rules
 */
const getUniqueSurchargeRules = (finals: Finals) => {
  if (!finals.surchargeDisplayProperties?.surchargeRules?.length) {
    return ''
  }

  const { surchargeRules } = finals.surchargeDisplayProperties

  const uniqueRules = [...new Set(surchargeRules)]

  // For capped surcharge, we don't want to display the rule
  if (uniqueRules.includes('')) {
    return ''
  }

  return uniqueRules
}

/**
 * Get translated surcharge label depends on the store settings and tax
 * @returns Surcharge label
 */
export const useTranslatedSurchargePaymentLabel = () => {
  const saleFinals = useAppSelector(selectSaleFinals)
  const t = useT()
  // Tax included text should be shown when tax is NOT zero
  const showTaxIncl = !vn(saleFinals.tax).eq(0)
  const surchargeRules = getUniqueSurchargeRules(saleFinals)
  const translationContext = 'Surcharge label in sale receipt preview'

  // No rules
  if (surchargeRules === '') {
    return showTaxIncl
      ? t('Payment surcharge (tax incl.)', { _context: translationContext })
      : t('Payment surcharge', { _context: translationContext })
  }

  // Single rule
  if (surchargeRules.length === 1) {
    return showTaxIncl
      ? t('Payment surcharge {rule} (tax incl.)', {
          rule: surchargeRules[0],
          _context: translationContext,
        })
      : t('Payment surcharge {rule}', {
          rule: surchargeRules[0],
          _context: translationContext,
        })
  }

  // Multiple rules
  return showTaxIncl
    ? t(
        '{ruleCount, plural, one {Surcharge {ruleCount} rate (tax incl.)} other {Payment surcharge {ruleCount} rates (tax incl.)}}',
        { ruleCount: surchargeRules.length, _context: translationContext }
      )
    : t(
        '{ruleCount, plural, one {Surcharge {ruleCount} rate} other {Payment surcharge {ruleCount} rates}}',
        { ruleCount: surchargeRules.length, _context: translationContext }
      )
}
